import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/auth.service';
import { usersStorage } from '../services/localStorage.service';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to clear auth data
  const clearAuth = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Initialize default admin user if needed
  useEffect(() => {
    usersStorage.initialize();
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');
      const isAuth = localStorage.getItem('isAuthenticated');

      if (token && storedUser && isAuth === 'true') {
        // Check if it's a dummy token (direct login without API)
        if (token === 'dummy-token-for-local-login') {
          // Direct login - verify user still exists in localStorage
          try {
            const userData = JSON.parse(storedUser);
            // Verify user still exists
            const user = usersStorage.getById(userData.id);
            if (user) {
              const { password: _, ...userWithoutPassword } = user;
              setUser(userWithoutPassword);
              setIsAuthenticated(true);
            } else {
              clearAuth();
            }
          } catch (error) {
            console.error('Error parsing user data:', error);
            clearAuth();
          }
        } else {
          // Real API token - verify with backend
          try {
            const response = await authService.getCurrentUser();
            setUser(response.data);
            setIsAuthenticated(true);
          } catch (error) {
            // Token invalid, clear storage
            clearAuth();
          }
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      const { accessToken, refreshToken, user: userData } = response.data;

      // Store tokens and user data
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');

      setUser(userData);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      console.error('Login error details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // More detailed error messages
      let errorMessage = 'Login failed';
      
      if (!error.response) {
        errorMessage = 'Cannot connect to server. Make sure backend is running on http://localhost:5000';
      } else if (error.response.status === 401) {
        errorMessage = error.response.data?.message || 'Invalid email or password';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Check backend logs.';
      } else {
        errorMessage = error.response.data?.message || `Login failed (${error.response.status})`;
      }
      
      return {
        success: false,
        message: errorMessage,
      };
    }
  };

  const logout = async () => {
    try {
      // Only call API logout if it's not a dummy token
      const token = localStorage.getItem('accessToken');
      if (token && token !== 'dummy-token-for-local-login') {
        await authService.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all auth data
      clearAuth();
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

